import { Component, OnInit, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { ProduktService, ServiceAftaleService } from 'src/app/api/services';
import { forkJoin, Subject } from 'rxjs';
import { ServiceaftaleTagDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-tag-dto';
import { ServiceaftaleStateDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-state-dto';
import { ServiceaftaleFilter } from '../models';
import { Params, Router, ActivatedRoute } from '@angular/router';
import { takeUntil, finalize } from 'rxjs/operators';
import { LoaderService } from 'src/app/services/loader.service';
import { PartnerBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-base-dto';
import { PartnerService } from 'src/app/api/services/partner.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';

@Component({
  selector: 'app-aftale-filter',
  templateUrl: './aftale-filter.component.html',
  styleUrls: ['./aftale-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AftaleFilterComponent implements OnInit, OnDestroy {
  products: ProduktDto[];
  tags: ServiceaftaleTagDto[];
  states: ServiceaftaleStateDto[];
  partners: PartnerBaseDto[];

  isLoading: boolean = false;

  @Input() filter: ServiceaftaleFilter;

  unsubscribe$ = new Subject<void>();

  constructor(
    private productService: ProduktService,
    private serviceaftaleService: ServiceAftaleService,
    private partnerService: PartnerService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private localStorage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    let loaderRef = this.loader.createLoader();
    loaderRef.showStateChanged$.subscribe((shouldShow) => {
      this.isLoading = shouldShow;
    });

    this.changeFilterParams();
    loaderRef.start();
    forkJoin(
      this.productService.getProdukter({
        caller: EGIS_SERVICE_CALLER,
      }),
      this.serviceaftaleService.getServiceaftaleTags({ caller: EGIS_SERVICE_CALLER }),
      this.serviceaftaleService.getServiceaftaleStates({ caller: EGIS_SERVICE_CALLER }),
      this.partnerService.getAllPartnere({ caller: EGIS_SERVICE_CALLER }),
    )
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          loaderRef.stop();
        }),
      )
      .subscribe(
        (response) => {
          this.products = response[0];
          this.tags = response[1];
          this.states = response[2];
          this.partners = response[3].filter((p) => p.active);
        },
        () => {},
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  filterCount() {
    return (
      this.filter.serviceaftaleTagIds.length +
      this.filter.serviceaftaleStateIds.length +
      this.filter.productIds.length +
      this.filter.partnerNos.length
    );
  }

  hasFilters(): boolean {
    return (
      this.filter.productIds.length > 0 ||
      this.filter.serviceaftaleStateIds.length > 0 ||
      this.filter.serviceaftaleTagIds.length > 0 ||
      this.filter.partnerNos.length > 0
    );
  }

  onProductToggle(id: ProduktEnum) {
    this.toggleValue(this.filter.productIds, id);
  }

  onStateToggle(id: ServiceaftaleStateEnum) {
    this.toggleValue(this.filter.serviceaftaleStateIds, id);
  }

  onTagToggle(id: number) {
    this.toggleValue(this.filter.serviceaftaleTagIds, id);
  }

  toggleValue(array: any[], id: any) {
    if (array.indexOf(id) > -1) {
      array.splice(array.indexOf(id), 1);
    } else {
      array.push(id);
    }
    this.changeFilterParams();
  }

  isProductChecked(id: ProduktEnum) {
    return this.filter.productIds.indexOf(id) > -1;
  }

  isStateChecked(id: ServiceaftaleStateEnum) {
    return this.filter.serviceaftaleStateIds.indexOf(id) > -1;
  }

  isTagChecked(id: number) {
    return this.filter.serviceaftaleTagIds.indexOf(id) > -1;
  }

  changeFilterParams() {
    const uniqueServiceaftaleTagIds = this.filter.serviceaftaleTagIds.filter((v, i, a) => a.indexOf(v) === i);
    const uniqueServiceaftaleStateIds = this.filter.serviceaftaleStateIds.filter((v, i, a) => a.indexOf(v) === i);
    const uniqueProductIds = this.filter.productIds.filter((v, i, a) => a.indexOf(v) === i);
    const uniquePartnerNos = this.filter.partnerNos.filter((v, i, a) => a.indexOf(v) === i);

    this.localStorage.set('aftale-filter', this.filter);

    const queryParams: Params = {
      serviceaftaleTagIds: this.filter.serviceaftaleTagIds.length > 0 ? uniqueServiceaftaleTagIds : null,
      serviceaftaleStateIds: this.filter.serviceaftaleStateIds.length > 0 ? uniqueServiceaftaleStateIds : null,
      productIds: this.filter.productIds.length > 0 ? uniqueProductIds : null,
      partnerNos: this.filter.partnerNos.length > 0 ? uniquePartnerNos : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  reset() {
    this.filter = {
      productIds: [],
      serviceaftaleStateIds: [],
      serviceaftaleTagIds: [],
      partnerNos: [],
    };
    this.changeFilterParams();
  }

  onPartnerChange(partnerNos: number[]) {
    this.filter.partnerNos = partnerNos;
    this.changeFilterParams();
  }
}
